import { defineStore } from "pinia";
import { ref } from "vue";
import * as amplitude from "@amplitude/analytics-browser";
import mixpanel from "mixpanel-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import { useConstant } from "@/utils/constant";
import { useShopStore } from "@/store/shop";
import * as Sentry from "@sentry/vue";

const isDev = import.meta.env.MODE === "development";
const isTest = import.meta.env.MODE === "test";

export const useAnalytics = defineStore("analytics", () => {
  const isAmplitudeInitialized = ref(false);
  const isMixpanelInitialized = ref(false);

  function initAnalytics(to) {
    if (!to) return;

    const isPlainUser = to.matched.some((route) =>
      ["Links", "DDTalkOrder", "AdjustTime", "ShopProfile"].includes(
        route.name,
      ),
    );

    if (!isPlainUser && !isAmplitudeInitialized.value) {
      const sdkKey =
        isDev || isTest
          ? "46e15a1ef0f104fc369724163351fd98"
          : isPlainUser
            ? "b424cf90bb6598a3bcb8bc1ac1a2faf7"
            : "32405797ae53da93cab28ebb02a6fe52";
      amplitude.init(sdkKey, {
        defaultTracking: {
          attribution: true,
          pageViews: true,
          sessions: true,
          formInteractions: true,
          fileDownloads: false,
        },
      });
      isAmplitudeInitialized.value = true;

      const { isJdelingApp } = useConstant();
      if (!isDev && !isTest && !isJdelingApp) {
        const sessionReplayTracking = sessionReplayPlugin({ sampleRate: 0.01 });
        amplitude.add(sessionReplayTracking);
      }
    }

    if (!isMixpanelInitialized.value) {
      const mixpanelToken =
        isDev || isTest
          ? "48f56d04c84cb31e137c20a409fb6d1e"
          : isPlainUser
            ? "9426a32a2469dbb42aa38839dadf167c"
            : "bbb0150a45958422417bad5869521039";
      mixpanel.init(mixpanelToken, {
        debug: isDev || isTest,
        track_pageview: true,
        persistence: "localStorage",
      });
      isMixpanelInitialized.value = true;
    }
  }

  function setUserId(id) {
    if (isAmplitudeInitialized.value) {
      const identifyEvent = new amplitude.Identify();
      amplitude.identify(identifyEvent);
      amplitude.setUserId(id);
    }
    if (isMixpanelInitialized.value) {
      mixpanel.identify(id);
    }
    setOptOut(false);
  }

  function setOptOut(value) {
    if (isAmplitudeInitialized.value) {
      amplitude.setOptOut(value);
    }
    if (isMixpanelInitialized.value) {
      if (value) {
        mixpanel.opt_out_tracking();
      } else {
        mixpanel.opt_in_tracking();
      }
    }
  }

  function setUserProperties(properties) {
    if (isAmplitudeInitialized.value) {
      const identifyEvent = new amplitude.Identify();
      Object.entries(properties).forEach(([key, value]) => {
        identifyEvent.set(key, value);
      });
      amplitude.identify(identifyEvent);
    }
    if (isMixpanelInitialized.value) {
      mixpanel.people.set(properties);
    }
  }

  function logEvent(name, properties) {
    const shopStore = useShopStore();
    const shopId = shopStore.shopId;
    const shopName = shopStore.shopData?.name_kr;

    if (isAmplitudeInitialized.value) {
      amplitude.track(name, {
        ...properties,
        shop_id: shopId,
        shop_name: shopName,
      });
    }
    if (isMixpanelInitialized.value) {
      mixpanel.track(name, {
        ...properties,
        shop_id: shopId,
        shop_name: shopName,
      });
    }
  }

  function logError(err) {
    Sentry.captureException(err);
  }

  return {
    initAnalytics,
    setUserId,
    setUserProperties,
    logEvent,
    setOptOut,
    logError,
  };
});
